import { useEffect, useState } from 'react';
import { z } from 'zod';
import {
  companyRoleSchema,
  operatorRoleSchema,
  invitationStatusSchema,
  uuidV7Schema,
} from '@cryptology-assist/domain';
import { ApiError, request, savedSchema, type Session } from './api';
import { ErrorNotice, useMutation } from './components';
import { dateLabel, type Copy, type Language } from './copy';
const detailsSchema = z.object({
  companyName: z.string(),
  email: z.email(),
  role: companyRoleSchema,
  bots: z.array(
    z.object({
      botId: uuidV7Schema,
      name: z.string(),
      role: operatorRoleSchema,
    })
  ),
  expiresAt: z.iso.datetime({ offset: true }),
  status: invitationStatusSchema,
});
const linkSchema = z.object({
  companyId: uuidV7Schema,
  invitationId: uuidV7Schema,
  token: z.string().min(32).max(200),
});
export function restoreInvitation() {
  const saved = window.sessionStorage.getItem('assist:invitation-return');
  if (!saved || window.location.pathname !== '/') return;
  const url = new URL(saved, window.location.origin);
  window.sessionStorage.removeItem('assist:invitation-return');
  if (url.origin === window.location.origin && url.pathname === '/invite')
    window.history.replaceState(null, '', url);
}
export function Invitation({
  session,
  providers,
  copy,
  language,
  reloadSession,
  authenticationLoading,
  authenticationError,
  retryAuthentication,
}: {
  session: Session | null;
  providers: readonly ('google' | 'microsoft')[];
  copy: Copy;
  language: Language;
  reloadSession: () => void;
  authenticationLoading: boolean;
  authenticationError: Error | null;
  retryAuthentication: () => void;
}) {
  const [link] = useState(() => {
    const url = new URL(window.location.href);
    return linkSchema.safeParse({
      companyId: url.searchParams.get('companyId'),
      invitationId: url.searchParams.get('invitationId'),
      token: url.hash.slice(1),
    });
  });
  const [details, setDetails] = useState<z.infer<typeof detailsSchema> | null>(
    null
  );
  const [error, setError] = useState<Error | null>(null);
  const mutation = useMutation();
  useEffect(() => {
    if (!link.success) {
      setError(new ApiError('INVITATION_NOT_AVAILABLE', 404));
      return;
    }
    const controller = new AbortController();
    void request(
      `/v1/invitations/${link.data.companyId}/${link.data.invitationId}/inspect`,
      detailsSchema,
      { body: { token: link.data.token }, signal: controller.signal }
    )
      .then(setDetails)
      .catch((error) => {
        if (!controller.signal.aborted)
          setError(
            error instanceof Error ? error : new Error('REQUEST_FAILED')
          );
      });
    return () => controller.abort();
  }, [link]);
  return (
    <main className="sign-in invitation-page">
      <a className="brand" href="/">
        {copy.product}
      </a>
      <h1>{copy.invitationReview}</h1>
      <ErrorNotice
        error={error ?? mutation.error ?? authenticationError}
        copy={copy}
        onRetry={retryAuthentication}
      />
      {!details && !error && <p>{copy.loading}</p>}
      {details && (
        <>
          <h2>{details.companyName}</h2>
          <p>
            {copy.invitationFor} <strong>{details.email}</strong>
          </p>
          <dl className="invitation-permissions">
            <dt>{copy.accountRole}</dt>
            <dd>{copy[details.role]}</dd>
            {details.bots.map((bot) => (
              <div key={bot.botId}>
                <dt>{bot.name}</dt>
                <dd>{copy[bot.role]}</dd>
              </div>
            ))}
          </dl>
          <p>
            {copy.expiresAt} {dateLabel(details.expiresAt, language)}
          </p>
          {session ? (
            <>
              <p>
                {copy.signedInAs} <strong>{session.name}</strong>
              </p>
              <button
                className="primary"
                disabled={mutation.busy || !link.success}
                onClick={() =>
                  void mutation.run(async () => {
                    if (!link.success)
                      throw new ApiError('INVITATION_NOT_AVAILABLE', 404);
                    await request(
                      `/v1/invitations/${link.data.companyId}/${link.data.invitationId}/accept`,
                      savedSchema,
                      { csrf: session.csrf, body: { token: link.data.token } }
                    );
                    window.sessionStorage.removeItem(
                      'assist:invitation-return'
                    );
                    reloadSession();
                    const url = new URL('/', window.location.origin);
                    url.searchParams.set('companyId', link.data.companyId);
                    if (details.bots[0])
                      url.searchParams.set('botId', details.bots[0].botId);
                    url.searchParams.set(
                      'view',
                      details.bots.length ? 'home' : 'accountAiProviders'
                    );
                    window.location.assign(url.href);
                  })
                }
              >
                {copy.acceptInvitation}
              </button>
              <button
                disabled={mutation.busy}
                onClick={() =>
                  void mutation.run(async () => {
                    window.sessionStorage.setItem(
                      'assist:invitation-return',
                      window.location.href
                    );
                    await request('/v1/logout', z.null(), {
                      csrf: session.csrf,
                      body: {},
                    });
                    reloadSession();
                  })
                }
              >
                {copy.useAnotherAccount}
              </button>
            </>
          ) : (
            <>
              <p>{copy.invitationSignInHelp}</p>
              <div className="connect-actions">
                {authenticationLoading && <p>{copy.loading}</p>}
                {!authenticationLoading &&
                  !authenticationError &&
                  providers.length === 0 && (
                    <p role="alert">{copy.noIdentityProviders}</p>
                  )}
                {providers.map((provider) => (
                  <a
                    key={provider}
                    href={`/v1/login/${provider}`}
                    onClick={() =>
                      window.sessionStorage.setItem(
                        'assist:invitation-return',
                        window.location.href
                      )
                    }
                  >
                    {copy[provider]}
                  </a>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </main>
  );
}
